import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    PlayIcon,
    PauseIcon,
    StopIcon,
    ChartBarIcon,
    TagIcon,
    FireIcon,
    GiftIcon,
    PercentBadgeIcon,
    ClockIcon,
    UserGroupIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import CampaignCreateModal from './CampaignCreateModal';

function CampaignManagementPanel() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        campaign_type: 'all',
        search: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    // Kampanya türleri
    const campaignTypes = {
        'flash_sale': {
            label: 'Flash Sale',
            icon: FireIcon,
            color: 'red',
            description: 'Sınırlı süre anlık indirimler'
        },
        'category_discount': {
            label: 'Kategori İndirimi',
            icon: TagIcon,
            color: 'blue',
            description: 'Belirli kategorilerde indirim'
        },
        'cart_discount': {
            label: 'Sepet İndirimi',
            icon: GiftIcon,
            color: 'green',
            description: 'Sepet tutarına göre indirim'
        },
        'coupon': {
            label: 'Kupon Kodu',
            icon: PercentBadgeIcon,
            color: 'purple',
            description: 'Kod ile kullanılan indirimler'
        },
        'bundle': {
            label: 'Paket İndirim',
            icon: UserGroupIcon,
            color: 'orange',
            description: '2 al 1 öde, paket fırsatları'
        },
        'special_day': {
            label: 'Özel Gün',
            icon: GiftIcon,
            color: 'pink',
            description: 'Anneler günü, yılbaşı vb.'
        }
    };

    // Kampanya durumları
    const statusOptions = {
        'draft': { label: 'Taslak', color: 'gray' },
        'active': { label: 'Aktif', color: 'green' },
        'paused': { label: 'Duraklatıldı', color: 'yellow' },
        'completed': { label: 'Tamamlandı', color: 'blue' },
        'cancelled': { label: 'İptal Edildi', color: 'red' }
    };

    useEffect(() => {
        fetchCampaigns();
    }, [filters, pagination.page]);

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            });

            // 'all' değerlerini kaldır
            if (filters.status === 'all') params.delete('status');
            if (filters.campaign_type === 'all') params.delete('campaign_type');
            if (!filters.search) params.delete('search');

            const response = await api(`/api/campaigns?${params}`);
            
            setCampaigns(response.data || []);
            setPagination(prev => ({
                ...prev,
                ...response.pagination
            }));

        } catch (error) {
            console.error('Kampanyalar yüklenirken hata:', error);
            toast.error(`Kampanyalar yüklenemedi: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (campaignId, newStatus) => {
        try {
            await api(`/api/campaigns/${campaignId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: newStatus })
            });

            toast.success('Kampanya durumu güncellendi');
            fetchCampaigns();
        } catch (error) {
            console.error('Durum güncellenirken hata:', error);
            toast.error('Durum güncellenemedi');
        }
    };

    const handleDelete = async (campaignId) => {
        if (!window.confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            await api(`/api/campaigns/${campaignId}`, {
                method: 'DELETE'
            });

            toast.success('Kampanya silindi');
            fetchCampaigns();
        } catch (error) {
            console.error('Kampanya silinirken hata:', error);
            toast.error('Kampanya silinemedi: ' + error.message);
        }
    };

    // Toplu seçim fonksiyonları
    const handleSelectCampaign = (campaignId) => {
        setSelectedCampaigns(prev => {
            if (prev.includes(campaignId)) {
                return prev.filter(id => id !== campaignId);
            } else {
                return [...prev, campaignId];
            }
        });
    };

    const handleSelectAll = () => {
        const deletableCampaigns = campaigns.filter(c => c.status !== 'active');
        const deletableIds = deletableCampaigns.map(c => c.id);
        
        if (selectedCampaigns.length === deletableIds.length && 
            deletableIds.every(id => selectedCampaigns.includes(id))) {
            // Tüm silinebilir kampanyalar seçili ise, seçimi temizle
            setSelectedCampaigns([]);
        } else {
            // Sadece silinebilir kampanyaları seç
            setSelectedCampaigns(deletableIds);
            if (campaigns.filter(c => c.status === 'active').length > 0) {
                toast.info('Sadece silinebilir kampanyalar seçildi. Aktif kampanyalar atlandı.');
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedCampaigns.length === 0) {
            toast.warning('Lütfen silinecek kampanyaları seçin');
            return;
        }

        const selectedCampaignData = campaigns.filter(c => selectedCampaigns.includes(c.id));
        const activeCampaigns = selectedCampaignData.filter(c => c.status === 'active');
        const deletableCampaigns = selectedCampaignData.filter(c => c.status !== 'active');

        // Aktif kampanya kontrolü
        if (activeCampaigns.length > 0) {
            const activeNames = activeCampaigns.map(c => c.name).join('\n');
            const deletableNames = deletableCampaigns.map(c => c.name).join('\n');
            
            let confirmMessage = `⚠️ UYARI: ${activeCampaigns.length} aktif kampanya silinemez!\n\n`;
            confirmMessage += `❌ Silinemeyen aktif kampanyalar:\n${activeNames}\n\n`;
            
            if (deletableCampaigns.length > 0) {
                confirmMessage += `✅ Silinebilecek kampanyalar (${deletableCampaigns.length} adet):\n${deletableNames}\n\n`;
                confirmMessage += `Sadece silinebilir kampanyaları silmek istiyor musunuz?`;
            } else {
                toast.error('Seçilen kampanyaların hepsi aktif durumda. Önce kampanyaları durdurun.');
                return;
            }

            if (!window.confirm(confirmMessage)) {
                return;
            }
        } else {
            const selectedCampaignNames = selectedCampaignData.map(c => c.name);
            if (!window.confirm(
                `Seçilen ${selectedCampaigns.length} kampanyayı silmek istediğinizden emin misiniz?\n\n` +
                `Silinecek kampanyalar:\n${selectedCampaignNames.join('\n')}`
            )) {
                return;
            }
        }

        setBulkDeleteLoading(true);
        try {
            const response = await api('/api/campaigns/bulk/delete', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    campaignIds: selectedCampaigns
                })
            });

            if (response.success) {
                toast.success(response.message);
                if (response.skippedActive > 0) {
                    toast.warning(`${response.skippedActive} aktif kampanya atlandı`);
                }
                setSelectedCampaigns([]);
                fetchCampaigns();
            } else {
                throw new Error(response.error || 'Bilinmeyen hata');
            }
        } catch (error) {
            console.error('Toplu silme hatası:', error);
            toast.error('Kampanyalar silinemedi: ' + error.message);
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    const showStats = async (campaignId) => {
        try {
            const response = await api(`/api/campaigns/${campaignId}/stats`);
            setSelectedCampaign(response.data);
            setShowStatsModal(true);
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
            toast.error('İstatistikler yüklenemedi');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusInfo = statusOptions[status] || statusOptions.draft;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                {statusInfo.label}
            </span>
        );
    };

    const getCampaignTypeBadge = (type) => {
        const typeInfo = campaignTypes[type] || campaignTypes.flash_sale;
        const IconComponent = typeInfo.icon;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-800`}>
                <IconComponent className="w-3 h-3 mr-1" />
                {typeInfo.label}
            </span>
        );
    };

    const getDiscountDisplay = (campaign) => {
        if (campaign.discount_type === 'percentage') {
            return `%${campaign.discount_value}`;
        } else if (campaign.discount_type === 'fixed_amount') {
            return formatCurrency(campaign.discount_value);
        } else {
            return campaign.discount_value;
        }
    };

    if (loading) {
        return <div className="text-center py-8">Kampanyalar yükleniyor...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Başlık ve Yeni Kampanya Butonu */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Kampanya Yönetimi</h1>
                    <p className="text-gray-600">Tüm kampanyalarınızı tek yerden yönetin</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green-light transition-colors"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Yeni Kampanya
                </button>
            </div>

            {/* Kampanya Türleri Özeti */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(campaignTypes).map(([key, type]) => {
                    const IconComponent = type.icon;
                    const count = campaigns.filter(c => c.campaign_type === key).length;
                    
                    return (
                        <div key={key} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-l-gray-400">
                            <div className="flex items-center justify-between mb-2">
                                <IconComponent className={`w-6 h-6 text-${type.color}-500`} />
                                <span className="text-2xl font-bold text-gray-800">{count}</span>
                            </div>
                            <h3 className="font-semibold text-gray-800 text-sm">{type.label}</h3>
                            <p className="text-xs text-gray-600">{type.description}</p>
                        </div>
                    );
                })}
            </div>

            {/* Filtreler */}
            <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <input
                        type="text"
                        placeholder="Kampanya ara..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                    
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    >
                        <option value="all">Tüm Durumlar</option>
                        {Object.entries(statusOptions).map(([key, status]) => (
                            <option key={key} value={key}>{status.label}</option>
                        ))}
                    </select>

                    <select
                        value={filters.campaign_type}
                        onChange={(e) => setFilters(prev => ({ ...prev, campaign_type: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    >
                        <option value="all">Tüm Türler</option>
                        {Object.entries(campaignTypes).map(([key, type]) => (
                            <option key={key} value={key}>{type.label}</option>
                        ))}
                    </select>

                    <div className="text-sm text-gray-600">
                        {pagination.total} kampanya bulundu
                    </div>
                </div>
            </div>

            {/* Kampanya Listesi */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Toplu İşlemler Başlığı */}
                {selectedCampaigns.length > 0 && (
                    <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-blue-900">
                                    {selectedCampaigns.length} kampanya seçildi
                                </span>
                                {(() => {
                                    const selectedCampaignData = campaigns.filter(c => selectedCampaigns.includes(c.id));
                                    const activeCount = selectedCampaignData.filter(c => c.status === 'active').length;
                                    const deletableCount = selectedCampaignData.filter(c => c.status !== 'active').length;
                                    
                                    return (
                                        <div className="flex items-center gap-2 text-xs">
                                            {deletableCount > 0 && (
                                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                                    {deletableCount} silinebilir
                                                </span>
                                            )}
                                            {activeCount > 0 && (
                                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                                                    {activeCount} aktif (silinemez)
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleteLoading}
                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    {bulkDeleteLoading ? 'Siliniyor...' : 'Seçilenleri Sil'}
                                </button>
                                <button
                                    onClick={() => setSelectedCampaigns([])}
                                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Seçimi Temizle
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={(() => {
                                                const deletableCampaigns = campaigns.filter(c => c.status !== 'active');
                                                const deletableIds = deletableCampaigns.map(c => c.id);
                                                return deletableIds.length > 0 && 
                                                       selectedCampaigns.length === deletableIds.length && 
                                                       deletableIds.every(id => selectedCampaigns.includes(id));
                                            })()}
                                            onChange={handleSelectAll}
                                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                                            title="Silinebilir kampanyaları seç/temizle"
                                        />
                                        <span className="text-xs text-gray-400">Silinebilir</span>
                                    </div>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Kampanya
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tür & Durum
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    İndirim
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tarih Aralığı
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Performans
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    İşlemler
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {campaigns.map((campaign) => (
                                <tr key={campaign.id} className={`hover:bg-gray-50 ${selectedCampaigns.includes(campaign.id) ? 'bg-blue-50' : ''} ${campaign.status === 'active' ? 'border-l-4 border-green-500' : ''}`}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="relative group">
                                            <input
                                                type="checkbox"
                                                checked={selectedCampaigns.includes(campaign.id)}
                                                onChange={() => handleSelectCampaign(campaign.id)}
                                                disabled={campaign.status === 'active'}
                                                className={`h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded ${
                                                    campaign.status === 'active' 
                                                        ? 'opacity-50 cursor-not-allowed' 
                                                        : 'cursor-pointer'
                                                }`}
                                                title={campaign.status === 'active' ? 'Aktif kampanyalar silinemez' : 'Kampanyayı seç'}
                                            />
                                            {campaign.status === 'active' && (
                                                <div className="absolute -top-8 left-0 bg-red-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                    Aktif kampanya silinemez
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {campaign.banner_image_url && (
                                                <img 
                                                    src={campaign.banner_image_url} 
                                                    alt={campaign.name}
                                                    className="w-10 h-10 rounded-md object-cover mr-3"
                                                />
                                            )}
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {campaign.name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {campaign.description}
                                                </div>
                                                {campaign.coupon_code && (
                                                    <div className="text-xs text-purple-600 font-mono">
                                                        Kod: {campaign.coupon_code}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="space-y-1">
                                            {getCampaignTypeBadge(campaign.campaign_type)}
                                            {getStatusBadge(campaign.status)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {getDiscountDisplay(campaign)}
                                        </div>
                                        {campaign.min_cart_amount > 0 && (
                                            <div className="text-xs text-gray-500">
                                                Min: {formatCurrency(campaign.min_cart_amount)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <ClockIcon className="w-4 h-4 mr-1" />
                                            <div>
                                                <div>{formatDate(campaign.start_date)}</div>
                                                <div>{formatDate(campaign.end_date)}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="space-y-1">
                                            <div className="flex items-center">
                                                <UserGroupIcon className="w-4 h-4 mr-1" />
                                                {campaign.total_usage_count || 0} kullanım
                                            </div>
                                            <div className="flex items-center">
                                                <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                                                {formatCurrency(campaign.total_revenue || 0)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            {/* Durum Değiştirme Butonları */}
                                            {campaign.status === 'draft' && (
                                                <button
                                                    onClick={() => handleStatusChange(campaign.id, 'active')}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Aktif Et"
                                                >
                                                    <PlayIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                            {campaign.status === 'active' && (
                                                <button
                                                    onClick={() => handleStatusChange(campaign.id, 'paused')}
                                                    className="text-yellow-600 hover:text-yellow-900"
                                                    title="Duraklat"
                                                >
                                                    <PauseIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                            {campaign.status === 'paused' && (
                                                <>
                                                    <button
                                                        onClick={() => handleStatusChange(campaign.id, 'active')}
                                                        className="text-green-600 hover:text-green-900"
                                                        title="Devam Et"
                                                    >
                                                        <PlayIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleStatusChange(campaign.id, 'cancelled')}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="İptal Et"
                                                    >
                                                        <StopIcon className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}

                                            {/* İstatistikler */}
                                            <button
                                                onClick={() => showStats(campaign.id)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="İstatistikler"
                                            >
                                                <ChartBarIcon className="w-4 h-4" />
                                            </button>

                                            {/* Düzenle */}
                                            <button
                                                onClick={() => setEditingCampaign(campaign)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                                title="Düzenle"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </button>

                                            {/* Sil */}
                                            {campaign.status !== 'active' && (
                                                <button
                                                    onClick={() => handleDelete(campaign.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Sil"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Sayfalama */}
                {pagination.totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                disabled={pagination.page === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Önceki
                            </button>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                                disabled={pagination.page === pagination.totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Sonraki
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                                    {' - '}
                                    <span className="font-medium">
                                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                                    </span>
                                    {' / '}
                                    <span className="font-medium">{pagination.total}</span>
                                    {' sonuç gösteriliyor'}
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                pageNum === pagination.page
                                                    ? 'z-10 bg-brand-green border-brand-green text-white'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {campaigns.length === 0 && (
                <div className="text-center py-12">
                    <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Henüz kampanya yok</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        İlk kampanyanızı oluşturarak başlayın.
                    </p>
                    <div className="mt-6">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-green hover:bg-brand-green-light"
                        >
                            <PlusIcon className="w-5 h-5 mr-2" />
                            Yeni Kampanya
                        </button>
                    </div>
                </div>
            )}

            {/* Kampanya Oluşturma Modal'ı */}
            <CampaignCreateModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={fetchCampaigns}
                editingCampaign={null}
            />
            
            {/* Kampanya Düzenleme Modal'ı */}
            <CampaignCreateModal
                isOpen={!!editingCampaign}
                onClose={() => setEditingCampaign(null)}
                onSuccess={() => {
                    fetchCampaigns();
                    setEditingCampaign(null);
                }}
                editingCampaign={editingCampaign}
            />
            
            {showStatsModal && selectedCampaign && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4">
                        <h3 className="text-lg font-bold mb-4">Kampanya İstatistikleri</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Toplam Kullanım</div>
                                    <div className="text-2xl font-bold">{selectedCampaign.campaign?.total_usage_count || 0}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Toplam Gelir</div>
                                    <div className="text-2xl font-bold">{formatCurrency(selectedCampaign.campaign?.total_revenue || 0)}</div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowStatsModal(false)}
                            className="mt-4 px-4 py-2 bg-brand-green text-white rounded-md hover:bg-brand-green-light"
                        >
                            Kapat
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CampaignManagementPanel;